import { useEffect, useState } from "react";
import { Footer } from "@/components/Footer";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, ChevronDown, HelpCircle, ChevronLeft, Lock } from "lucide-react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { EmailLink } from "@/components/EmailLink";

const faqs = [
  {
    question: "How do I report a security vulnerability to ClearMakes?",
    answer: "Please email security@clearmakes.com with a clear description, the steps to reproduce it, and the possible impact. We aim to reply within 3 business days."
  },
  {
    question: "Does ClearMakes offer a bug bounty program?",
    answer: "No. We do not offer money rewards at this time, but we provide a safe harbor for researchers who follow this policy."
  },
  {
    question: "Is user data protected during security testing?",
    answer: "Yes. Our rules of engagement strictly prohibit accessing, modifying, or deleting any data that is not your own. Researchers must use their own test accounts to verify findings."
  }
];

const Vulnerability = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  // FAQ Schema Markup (JSON-LD)
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
          <HelmetProvider>
        <Helmet>
          <title>Vulnerability Disclosure Policy | ClearMakes</title>
          <meta name="description" content="How to report a security problem in ClearMakes responsibly, what we promise in return, and how we respond to protect the people who use our products." />
        </Helmet>
      </HelmetProvider>
<div className="min-h-screen bg-[#0A0F1C] text-foreground font-sans flex flex-col relative selection:bg-primary/30">
      {/* Inject JSON-LD Schema directly into the component */}
      
        
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@graph": [
                {
                  "@type": "WebSite",
                  "@id": "https://clearmakes.com/#website",
                  "url": "https://clearmakes.com/",
                  "name": "ClearMakes",
                  "description": "The Professional Workspace for unified messaging and AI communication.",
                  "publisher": { "@id": "https://clearmakes.com/#organization" }
                },
                {
                  "@type": "Organization",
                  "@id": "https://clearmakes.com/#organization",
                  "name": "ClearMakes",
                  "url": "https://clearmakes.com/",
                  "logo": { "@type": "ImageObject", "url": "https://clearmakes.com/logo.png" },
                  "sameAs": [
                    "https://github.com/ClearMakes"
                  ]
                },
                {
                  "@type": "SoftwareApplication",
                  "@id": "https://clearmakes.com/#software",
                  "name": "ClearMakes Workspace",
                  "applicationCategory": "BusinessApplication",
                  "operatingSystem": "Web",
                  "url": "https://clearmakes.com/",
                  "offers": { "@type": "Offer", "price": "0.00", "priceCurrency": "USD" }
                }
              ]
            })
          }}
        />

      <div className="absolute inset-0 noise-texture opacity-20 pointer-events-none" />
      

      <main className="flex-grow pt-32 pb-20 container relative z-10 max-w-4xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-6 border border-primary/20">
            <ShieldCheck className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white tracking-tight">Security at ClearMakes</h1>
          <p className="text-lg text-muted-foreground">Our Vulnerability Disclosure Policy (VDP)</p>
          <p className="text-sm text-gray-500 mt-2 font-mono uppercase tracking-widest">Last Updated: September 28, 2026</p>
        </div>

        <div className="space-y-10 text-gray-300 glass-panel border border-white/5 rounded-[2.5rem] p-8 md:p-16">
          <section>
            <p className="leading-relaxed text-lg text-muted-foreground">
              The security of our systems is our highest priority. We welcome the help of security researchers
              in identifying potential vulnerabilities. This policy explains how to report an issue. Our{" "}
              <a href="/security-architecture" className="text-primary hover:underline">security page</a> describes how we protect your data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-6 flex items-center gap-3">
                <Lock className="w-6 h-6 text-primary" />
                How to Report a Security Vulnerability
            </h2>
            <p className="mb-4 leading-relaxed">
              If you believe you have discovered a security vulnerability in a ClearMakes service, please
              report it to us at:
            </p>
            <div className="bg-white/5 p-6 rounded-2xl border border-white/10 mb-6">
                <EmailLink email="security@clearmakes.com" className="break-all text-xl font-bold text-primary sm:text-2xl hover:text-white transition-colors">
                    security@clearmakes.com
                </EmailLink>
            </div>

            <p className="mb-3 font-semibold text-white">Please include the following details in your report:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>A clear description of the vulnerability, including its potential impact on user data.</li>
              <li>Detailed steps to reproduce the issue, including any URLs or code snippets.</li>
              <li>How we can contact you to follow up.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Our Commitment ("Safe Harbor")</h2>
            <p className="mb-4 leading-relaxed">
              We will not take legal action against or suspend accounts for research conducted
              in accordance with this policy. We consider security research into our 
              <a href="/integrations" className="text-primary hover:underline mx-1">platform integrations</a> to be authorized and beneficial to the community.
            </p>

            <div className="grid md:grid-cols-2 gap-4 mt-6">
                {[
                    "A reply within 3 business days",
                    "An estimate of when it will be fixed",
                    "A message when it is fixed",
                    "No legal action for good-faith research"
                ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 p-4 rounded-xl bg-white/[0.02] border border-white/5">
                        <div className="w-2 h-2 rounded-full bg-primary" />
                        <span className="text-sm">{item}</span>
                    </div>
                ))}
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Rules of Engagement</h2>
            <div className="bg-primary/5 p-6 rounded-2xl border border-primary/20 space-y-4">
                <p className="text-sm leading-relaxed text-white/80">
                  To ensure the safety of our users, we ask that you strictly avoid:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-sm text-white/70">
                  <li>Accessing, modifying, or deleting any user data that is not your own.</li>
                  <li>Denial of Service (DoS) attacks or actions that disrupt the service.</li>
                  <li>Social engineering or phishing attacks against our employees or users.</li>
                  <li>Making vulnerability information public before we have had reasonable time to fix it.</li>
                </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Exclusions</h2>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Automated scanner results without a working proof-of-concept (PoC).</li>
              {/* Fixed: Pointing explicitly to the /security-architecture route from your list */}
              <li>Missing security headers without a demonstrated impact on our <a href="/security-architecture" className="text-primary hover:underline">security architecture</a>.</li>
              <li>Self-XSS (Cross-Site Scripting) without a lateral attack vector.</li>
              <li>Disclosures of publicly known software versions.</li>
            </ul>
          </section>
        </div>

        {/* FAQ SCHEMA SECTION (Strict Requirement) */}
        <section className="mt-20 max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <HelpCircle className="w-12 h-12 text-primary mx-auto mb-4 opacity-80" />
            <h2 className="text-3xl font-bold text-white mb-4">Researcher FAQs</h2>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] rounded-2xl overflow-hidden transition-colors duration-300">
                <button
                  className="w-full px-8 py-6 text-left flex justify-between items-center focus:outline-none"
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                >
                  <span className="font-semibold text-white pr-8 text-lg">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-primary flex-shrink-0 transition-transform duration-300 ${openFaqIndex === index ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence initial={false}>
                  {openFaqIndex === index && (
                    <motion.section
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-8 pb-8 text-muted-foreground leading-relaxed border-t border-white/5 pt-6">
                        {faq.answer}
                      </div>
                    </motion.section>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </section>

        <div className="pt-16 text-center">
          <a href="/" className="text-primary hover:text-white transition-colors font-medium flex items-center justify-center gap-2">
            <ChevronLeft size={16} /> Back to Home
          </a>
        </div>
      </main>

      <Footer />
    </div>
    </>
  );
};

export default Vulnerability;